/**
 * Relógio do servidor para os web apps — a única fonte de "agora" do web.
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Por quê: CLAUDE.md, princípio 4 — tempo = servidor; o relógio do sistema
 * nunca decide nada com efeito persistente. O web é sempre online (decisão do
 * owner, 2026-10-20), então não há GPS nem âncora offline: só o servidor. Antes
 * disto, `useLabelOps.ts` gravava `Date.now()` do browser em
 * `statusChangedAtMillis` (o trigger `onLabelWritten` usa esse campo no
 * `daily_ops`, #230) e ~150 pontos liam o relógio do aparelho.
 *
 * Contrato: o clock guarda uma ÂNCORA `{ serverMs, perfMs }` e responde
 * `now() = serverMs + (performance.now() − perfMs)` — monotônico, então o
 * usuário mexer no relógio do aparelho depois do boot não muda nada. A âncora
 * vem das `sources`, tentadas em ordem; a primeira que responde vence, com o
 * ponto médio da ida e volta (`rtt / 2`) somado. Re-sincroniza no boot, a cada
 * `resyncMs` e quando o aparelho "dorme" (ver abaixo). `now()` ANTES da 1ª
 * âncora cai em `Date.now()` — só apresentação — e avisa por `onFallback`
 * (telemetria) apenas se a sincronização já falhou por completo: toda GRAVAÇÃO passa por `assertCanWrite()`
 * (`requiresInternet.ts`), que recusa enquanto `isReady()` for falso. Sem
 * efeito colateral além da rede das fontes.
 *
 * Usado por: `configureServerClock`/`serverNow` (singleton por aba), chamados
 * pelos `lib/serverClock.ts` de `apps/app`, `apps/admin` e `apps/crm`;
 * `requiresInternet.ts`; `ConnectivityBanner.tsx`.
 *
 * Arquitetura: núcleo puro — fontes, `performance.now`, `Date.now`, agenda e
 * visibilidade chegam por `deps`, e o teste roda sem rede nem timers reais.
 *
 * Falhas conhecidas / armadilhas:
 *  - `performance.now()` pode PARAR enquanto o computador dorme (Chrome/macOS):
 *    a âncora ficaria atrasada pelo tempo de sono. Por isso `now()` compara o
 *    avanço de `performance.now()` com o de `Date.now()` desde a âncora; se
 *    divergem mais de `DRIFT_RESYNC_MS`, dispara re-sincronização (limitada por
 *    `MIN_RESYNC_GAP_MS`). O `Date.now()` serve só de GATILHO — nunca é
 *    devolvido como hora.
 *  - Fonte `httpDate` tem resolução de 1 s (cabeçalho `Date`). Serve para
 *    boot/pré-login; a callable `getServerTime` (ms) a substitui assim que há
 *    sessão.
 *
 * Dados — callable `getServerTime` (`apps/functions/src/getServerTime.ts`):
 *   resposta `{ "now": 1792504800123 }` (epoch ms UTC do servidor).
 *
 * Issues: #1010 (esta migração), #1009 (relógio nativo), #882, #230.
 *
 * Histórico:
 *  - 2026-10-20 15:22 +0200 — (#1010) criado.
 */

/** Uma fonte devolve o epoch ms do servidor "agora" (ou lança). */
export type ServerClockSource = () => Promise<number>;

export type ServerClockDeps = {
  /** Em ordem de preferência; a primeira que não lança vence. */
  sources: readonly ServerClockSource[];
  /** `performance.now()` — monotônico. */
  perfNow: () => number;
  /** `Date.now()` — SÓ para detectar sono do aparelho; nunca vira hora. */
  wallNow: () => number;
  /** Agenda `fn` em `ms`; devolve o cancelamento. */
  schedule: (fn: () => void, ms: number) => () => void;
  /** Aba visível? Escondida, o re-sync periódico pausa. */
  isVisible: () => boolean;
  /** Re-sincronização periódica (padrão 10 min). */
  resyncMs?: number;
  /** `now()` antes da 1ª âncora (telemetria); chamado no máximo uma vez. */
  onFallback?: (() => void) | undefined;
  /** Todas as fontes falharam numa tentativa (telemetria). */
  onSyncFailed?: ((error: unknown) => void) | undefined;
};

export type ServerClock = {
  /** Epoch ms do servidor; antes da âncora devolve `Date.now()` (só apresentação). */
  now: () => number;
  /** Já há âncora do servidor? */
  isReady: () => boolean;
  /** Fonte da âncora atual: índice em `sources` (0 = melhor) ou `null`. */
  sourceIndex: () => number | null;
  /** Sincroniza agora (auth mudou, voltou a rede…). Nunca lança. */
  sync: () => Promise<void>;
  subscribe: (listener: () => void) => () => void;
  /** Resolve `true` quando há âncora, ou `false` após `timeoutMs` sem ela. Nunca rejeita. */
  whenReady: (timeoutMs: number) => Promise<boolean>;
  /** Para os timers (teste/desmontagem). */
  dispose: () => void;
};

/** Divergência entre `performance.now` e `Date.now` que indica sono/relógio mexido. */
export const DRIFT_RESYNC_MS = 2_000;
/** Intervalo mínimo entre re-syncs disparados por drift (relógio mexido não pode martelar a rede). */
export const MIN_RESYNC_GAP_MS = 30_000;
export const DEFAULT_RESYNC_MS = 10 * 60_000;
/** Depois de uma falha total, tenta de novo cedo — sem âncora o app não grava. */
export const RETRY_AFTER_FAILURE_MS = 3_000;

type Anchor = { serverMs: number; perfMs: number; wallMs: number; source: number };

/**
 * Cria o relógio do servidor (núcleo puro; o singleton do navegador é `configureServerClock`).
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Usado por: `configureServerClock` (aqui) e `serverClock.test.ts`.
 */
export function createServerClock(deps: ServerClockDeps): ServerClock {
  let anchor: Anchor | null = null;
  let fallbackReported = false;
  // Quantas tentativas terminaram com TODAS as fontes falhando (zera ao ancorar).
  let syncFailures = 0;
  let inFlight: Promise<void> | null = null;
  let lastSyncStartedPerf = Number.NEGATIVE_INFINITY;
  let cancelTimer: (() => void) | null = null;
  const listeners = new Set<() => void>();
  const resyncMs = deps.resyncMs ?? DEFAULT_RESYNC_MS;

  /** Avisa os assinantes que a âncora mudou. Origem: #1010 — 2026-10-20 15:22 +0200. Usado por: `doSync`. */
  const emit = (): void => {
    for (const l of listeners) l();
  };

  /** Agenda o próximo re-sync (pula a rodada com a aba escondida). Origem: #1010 — 2026-10-20 15:22 +0200. Usado por: `doSync`. */
  const scheduleNext = (ms: number): void => {
    cancelTimer?.();
    cancelTimer = deps.schedule(() => {
      // Aba escondida: pula esta rodada; `visibilitychange` (quem liga o clock)
      // chama `sync()` ao voltar.
      if (!deps.isVisible()) {
        scheduleNext(resyncMs);
        return;
      }
      void sync();
    }, ms);
  };

  /**
   * Tenta as fontes em ordem e firma a âncora (ponto médio do RTT).
   *
   * Origem: #1010 — 2026-10-20 15:22 +0200. Usado por: `sync`.
   */
  const doSync = async (): Promise<void> => {
    lastSyncStartedPerf = deps.perfNow();
    let lastError: unknown = null;
    for (const [i, source] of deps.sources.entries()) {
      const t0 = deps.perfNow();
      try {
        const serverMs = await source();
        const t1 = deps.perfNow();
        if (!Number.isFinite(serverMs) || serverMs <= 0) throw new Error('server_time_invalid');
        // Ponto médio: assume ida e volta simétricas (mesma hipótese do NTP simples).
        anchor = { serverMs: serverMs + (t1 - t0) / 2, perfMs: t1, wallMs: deps.wallNow(), source: i };
        syncFailures = 0;
        emit();
        scheduleNext(resyncMs);
        return;
      } catch (e) {
        lastError = e;
      }
    }
    syncFailures += 1;
    deps.onSyncFailed?.(lastError);
    scheduleNext(RETRY_AFTER_FAILURE_MS);
  };

  /** Sincroniza agora, um voo por vez. Origem: #1010 — 2026-10-20 15:22 +0200. Usado por: boot, `now` (drift), gatilhos do navegador e `lib/serverClock.ts` dos três web apps. */
  const sync = (): Promise<void> => {
    // Uma sincronização por vez: vários gatilhos juntos (auth + online + timer)
    // compartilham o mesmo voo.
    inFlight ??= doSync().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  /**
   * Epoch ms do servidor (âncora + `performance.now`); antes da âncora cai no relógio do aparelho só para apresentação.
   *
   * Origem: #1010 — 2026-10-20 15:22 +0200. Usado por: `serverNow`/`serverDate` e `requiresInternet.ts`.
   */
  const now = (): number => {
    if (anchor === null) {
      // Só reporta quando a sincronização JÁ falhou por completo: no boot normal
      // a 1ª leitura cai aqui por ~100–300 ms antes da âncora chegar, e reportar
      // isso seria um evento por carga de página (ruído no PostHog).
      if (syncFailures > 0 && !fallbackReported) {
        fallbackReported = true;
        deps.onFallback?.();
      }
      return deps.wallNow();
    }
    const perfElapsed = deps.perfNow() - anchor.perfMs;
    const wallElapsed = deps.wallNow() - anchor.wallMs;
    if (
      Math.abs(wallElapsed - perfElapsed) > DRIFT_RESYNC_MS
      && deps.perfNow() - lastSyncStartedPerf > MIN_RESYNC_GAP_MS
    ) {
      void sync();
    }
    // Bug corrigido em 2026-10-21 00:25 +0200 (#1010; achado pela Super Suíte de telas, #1011): `perfNow()` é
    // fracionário (microssegundos) e o `rtt / 2` da âncora também, então `now()` devolvia ms com casas decimais
    // (ex.: 1792535096265.4). Esse valor vira `baixas[].atMillis` e `statusChangedAtMillis`, e as rules exigem
    // `entry.atMillis is int` — TODA baixa/estorno de etiqueta feito no web levava `permission-denied`
    // (mensagem enganosa: "maximum of 1000 expressions", que é como o emulador nega uma escrita inválida).
    // `Math.trunc` mantém a monotonicidade e dá o inteiro que o contrato (`z.number().int()`) pede.
    // Não voltar a devolver o float "mais preciso".
    return Math.trunc(anchor.serverMs + perfElapsed);
  };

  // Sincroniza já no boot; falha total reagenda cedo (ver `doSync`).
  void sync();

  return {
    now,
    isReady: () => anchor !== null,
    sourceIndex: () => anchor?.source ?? null,
    sync,
    subscribe(listener): () => void {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
    whenReady(timeoutMs): Promise<boolean> {
      if (anchor !== null) return Promise.resolve(true);
      return new Promise<boolean>((resolve) => {
        let done = false;
        let unsubscribe: () => void = () => undefined;
        let cancel: () => void = () => undefined;
        const finish = (ok: boolean): void => {
          if (done) return;
          done = true;
          unsubscribe();
          cancel();
          resolve(ok);
        };
        unsubscribe = this.subscribe(() => {
          if (anchor !== null) finish(true);
        });
        cancel = deps.schedule(() => {
          finish(false);
        }, timeoutMs);
      });
    },
    dispose(): void {
      cancelTimer?.();
      cancelTimer = null;
    },
  };
}

/**
 * Fonte de fallback: cabeçalho HTTP `Date` de uma requisição ao PRÓPRIO
 * host (mesma origem, então o cabeçalho é legível sem CORS). Resolução de 1 s
 * — soma 500 ms para centrar o truncamento. Funciona sem login, por isso é a
 * fonte do boot; o iOS usa o mesmo truque (`ServerClockAnchor`).
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Contrato: `HEAD <path>` com `cache: 'no-store'`; lança se não houver
 * cabeçalho `Date` válido. O service worker ignora HEAD (só intercepta GET),
 * então a resposta nunca vem do cache.
 *
 * Usado por: `lib/serverClock.ts` dos três web apps.
 */
export function httpDateSource(
  path = '/',
  fetchImpl: typeof fetch = (...a) => fetch(...a),
): ServerClockSource {
  return async () => {
    const res = await fetchImpl(path, { method: 'HEAD', cache: 'no-store', credentials: 'omit' });
    const header = res.headers.get('date');
    const ms = header === null ? Number.NaN : Date.parse(header);
    if (!Number.isFinite(ms)) throw new Error('http_date_missing');
    return ms + 500;
  };
}

// --- Singleton por aba -----------------------------------------------------

let instance: ServerClock | null = null;

/**
 * Liga o relógio do servidor deste app (uma vez, no boot, antes do render).
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Contrato: idempotente — a 2ª chamada devolve a instância existente. Liga os
 * gatilhos de re-sync do navegador: aba volta a ficar visível e evento
 * `online`. Usado por: `lib/serverClock.ts` de `apps/app`, `admin` e `crm`.
 */
export function configureServerClock(opts: {
  sources: readonly ServerClockSource[];
  onFallback?: (() => void) | undefined;
  onSyncFailed?: ((error: unknown) => void) | undefined;
}): ServerClock {
  if (instance !== null) return instance;
  const clock = createServerClock({
    sources: opts.sources,
    perfNow: () => performance.now(),
    wallNow: () => Date.now(),
    schedule: (fn, ms) => {
      const id = setTimeout(fn, ms);
      return (): void => {
        clearTimeout(id);
      };
    },
    isVisible: () => document.visibilityState === 'visible',
    onFallback: opts.onFallback,
    onSyncFailed: opts.onSyncFailed,
  });
  if (typeof window !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void clock.sync();
    });
    window.addEventListener('online', () => {
      void clock.sync();
    });
  }
  instance = clock;
  return clock;
}

/**
 * Singleton configurado, ou `null` (teste/SSR).
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Usado por: `requiresInternet.ts` (`currentBlockReason`, `useRequiresInternet`) e `serverNow`.
 */
export function getServerClock(): ServerClock | null {
  return instance;
}

/**
 * "Agora" do web, em epoch ms do SERVIDOR. Use no lugar de `Date.now()` e de
 * `new Date()`. Sem clock configurado (teste unitário) devolve `Date.now()`.
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200. Usado por: todo o código do web que
 * precisa de "agora" (gravação, decisão e exibição).
 */
export function serverNow(): number {
  return instance === null ? Date.now() : instance.now();
}

/**
 * `new Date(serverNow())` — para APIs que querem `Date`.
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Usado por: o código web que antes escrevia `new Date()` (default de parâmetro, formatação, janelas de dia).
 */
export function serverDate(): Date {
  return new Date(serverNow());
}

/**
 * Descarta o singleton (só teste).
 *
 * Origem: #1010 — 2026-10-20 15:22 +0200.
 *
 * Usado por: testes de `ui-shared` e `apps/app` (`useLabelOps.test.ts`, `ConnectivityBanner.test.tsx`).
 */
export function resetServerClockForTest(): void {
  instance?.dispose();
  instance = null;
}

/** Quanto o boot espera a âncora antes de renderizar mesmo assim (offline/lento). */
export const SERVER_CLOCK_BOOT_WAIT_MS = 2_000;

/**
 * Renderiza o app DEPOIS da âncora de hora (ou após `SERVER_CLOCK_BOOT_WAIT_MS`).
 *
 * Origem: #1010 — 2026-10-20 16:05 +0200 (auditoria global 2: estado inicial capturado no 1º
 * render — `useState(() => serverNow())`, `useMemo(..., [])` — usava o
 * relógio do aparelho para a sessão inteira, justamente quando ele está errado).
 *
 * Por quê: a âncora vem de um `HEAD` à própria origem (dezenas de ms); esperar
 * por ela antes do 1º render garante que tudo o que congela "agora" nasce com
 * a hora do servidor, sem remontar a árvore depois. Se a rede não responde, o
 * app sobe mesmo assim em `SERVER_CLOCK_BOOT_WAIT_MS`: gravação segue bloqueada
 * (`CLOCK_PENDING`) e a faixa aparece, em vez de tela branca.
 *
 * Contrato: sem clock configurado (teste) renderiza na hora. Nunca lança.
 *
 * Usado por: `main.tsx` de `apps/app`, `admin`, `crm` e `developers`.
 */
export function renderAfterServerClock(render: () => void, timeoutMs = SERVER_CLOCK_BOOT_WAIT_MS): void {
  const clock = getServerClock();
  if (clock === null) {
    render();
    return;
  }
  void clock.whenReady(timeoutMs).then(render);
}
